<template><div><BlogPost :author="$frontmatter.author" :posted="parse($frontmatter.posted, 'y-M-d', new Date())" :heading="$frontmatter.heading" :excerpt="$frontmatter.excerpt">
<h2 id="tldr" tabindex="-1" class="d-docsite--header-2">TLDR <a class="header-anchor d-link d-docsite--link d-link" href="#tldr" aria-hidden="true">#</a></h2>
<dt-notice kind="warning" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
<strong>Breaking change:</strong> Two token families are being deprecated in favor of purpose-built replacements.
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><code v-pre>--dt-size-*</code> -&gt; <code v-pre>--dt-layout-*</code> for layout use (widths, heights -- 64px base unit)</li>
<li class="d-docsite--list-element"><code v-pre>--dt-space-*</code> -&gt; <code v-pre>--dt-spacing-*</code> for spacing use (padding, margin, gap -- 8px base unit)</li>
<li class="d-docsite--list-element">Old tokens still compile (deprecated aliases) but will be removed in a future major version</li>
<li class="d-docsite--list-element">Migration tool available: <code v-pre>npx dialtone-migration-helper</code></li>
<li class="d-docsite--list-element">The previous <code v-pre>space-to-size</code> config has been <strong>removed</strong> -- use the new configs instead</li>
</ul>
</dt-notice>
<h2 id="why-two-systems" tabindex="-1" class="d-docsite--header-2">Why Two Systems? <a class="header-anchor d-link d-docsite--link d-link" href="#why-two-systems" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Spacing and layout have fundamentally different size requirements, and a single token scale can't serve both well at the same time.</p>
<p class="d-docsite--paragraph">Spacing — padding, margin, gap — operates at small increments. You need 2px, 4px, 6px, 8px, 12px. An 8px base unit gives you exactly that: logical, fine-grained steps at the low end of the scale where whitespace decisions actually live. Layout — widths, heights, structural dimensions — operates at much larger values where that same granularity isn't useful. A 64px base unit produces a scale that matches how layout sizes actually work in practice.</p>
<p class="d-docsite--paragraph">The old <code v-pre>--dt-size-*</code> and <code v-pre>--dt-space-*</code> tokens were the same values shared across both contexts. This worked reasonably well at the low end of the scale — those small stops mapped well enough to spacing use cases — but once you reached the larger values, the scale became awkward. There was a jarring jump around the 600px range where the stops shifted into territory that only made sense for layout, yet the tokens carried no signal about which context they belonged to. The intent was blurred. Spacing and layout are different efforts, and they deserve different tools.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong><code v-pre>--dt-spacing-*</code></strong> — 8px base unit, fine-grained steps for padding, margin, gap, and inset</li>
<li class="d-docsite--list-element"><strong><code v-pre>--dt-layout-*</code></strong> — 64px base unit, larger steps for widths, heights, and structural dimensions</li>
</ul>
<h2 id="dt-layout-tokens" tabindex="-1" class="d-docsite--header-2"><code v-pre>--dt-layout-*</code> Tokens <a class="header-anchor d-link d-docsite--link d-link" href="#dt-layout-tokens" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use layout tokens for <code v-pre>width</code>, <code v-pre>height</code>, <code v-pre>min-width</code>, <code v-pre>max-width</code>, <code v-pre>min-height</code>, <code v-pre>max-height</code>, and similar structural properties.</p>
<p class="d-docsite--paragraph"><strong>Base unit:</strong> <code v-pre>--dt-layout-base</code> = 64px. Scale stops multiply the base (e.g., <code v-pre>layout-100</code> = 64px, <code v-pre>layout-200</code> = 128px).</p>
<h3 id="mapping-from-dt-size" tabindex="-1" class="d-docsite--header-3">Mapping from <code v-pre>--dt-size-*</code> <a class="header-anchor d-link d-docsite--link d-link" href="#mapping-from-dt-size" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>Old Token</th>
<th>Old Value</th>
<th>New Token</th>
<th>New Value</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>--dt-size-500</code></td>
<td>16px</td>
<td><code v-pre>--dt-layout-25</code></td>
<td>16px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-600</code></td>
<td>32px</td>
<td><code v-pre>--dt-layout-50</code></td>
<td>32px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-650</code></td>
<td>48px</td>
<td><code v-pre>--dt-layout-75</code></td>
<td>48px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-700</code></td>
<td>64px</td>
<td><code v-pre>--dt-layout-100</code></td>
<td>64px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-750</code></td>
<td>96px</td>
<td><code v-pre>--dt-layout-150</code></td>
<td>96px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-800</code></td>
<td>128px</td>
<td><code v-pre>--dt-layout-200</code></td>
<td>128px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-850</code></td>
<td>192px</td>
<td><code v-pre>--dt-layout-300</code></td>
<td>192px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-900</code></td>
<td>256px</td>
<td><code v-pre>--dt-layout-400</code></td>
<td>256px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-950</code></td>
<td>384px</td>
<td><code v-pre>--dt-layout-600</code></td>
<td>384px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-1000</code></td>
<td>512px</td>
<td><code v-pre>--dt-layout-800</code></td>
<td>512px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-1050</code></td>
<td>768px</td>
<td><code v-pre>--dt-layout-1200</code></td>
<td>768px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-1100</code></td>
<td>1024px</td>
<td><code v-pre>--dt-layout-1600</code></td>
<td>1024px</td>
</tr>
</tbody>
</table>
<h3 id="nearest-neighbor-mappings" tabindex="-1" class="d-docsite--header-3">Nearest-neighbor mappings <a class="header-anchor d-link d-docsite--link d-link" href="#nearest-neighbor-mappings" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">These old tokens have no exact equivalent in the new scale. The migration tool maps them to the closest available stop.</p>
<table>
<thead>
<tr>
<th>Old Token</th>
<th>Old Value</th>
<th>New Token</th>
<th>New Value</th>
<th>Δ</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>--dt-size-825</code></td>
<td>164px</td>
<td><code v-pre>--dt-layout-250</code></td>
<td>160px</td>
<td>4px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-875</code></td>
<td>216px</td>
<td><code v-pre>--dt-layout-350</code></td>
<td>224px</td>
<td>8px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-905</code></td>
<td>264px</td>
<td><code v-pre>--dt-layout-400</code></td>
<td>256px</td>
<td>8px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-925</code></td>
<td>332px</td>
<td><code v-pre>--dt-layout-500</code></td>
<td>320px</td>
<td>12px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-975</code></td>
<td>464px</td>
<td><code v-pre>--dt-layout-700</code></td>
<td>448px</td>
<td>16px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-1020</code></td>
<td>628px</td>
<td><code v-pre>--dt-layout-1000</code></td>
<td>640px</td>
<td>12px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-1040</code></td>
<td>764px</td>
<td><code v-pre>--dt-layout-1200</code></td>
<td>768px</td>
<td>4px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-1060</code></td>
<td>828px</td>
<td><code v-pre>--dt-layout-1300</code></td>
<td>832px</td>
<td>4px</td>
</tr>
<tr>
<td><code v-pre>--dt-size-1080</code></td>
<td>912px</td>
<td><code v-pre>--dt-layout-1400</code></td>
<td>896px</td>
<td>16px</td>
</tr>
</tbody>
</table>
<h3 id="tokens-converted-to-raw-values" tabindex="-1" class="d-docsite--header-3">Tokens converted to raw values <a class="header-anchor d-link d-docsite--link d-link" href="#tokens-converted-to-raw-values" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The following tokens exceed the layout scale (max <code v-pre>--dt-layout-1600</code> = 1024px) and have no token equivalent. The migration tool converts them to raw <code v-pre>rem</code> values and adds a <code v-pre>TODO</code> comment so they can be updated when a token is added.</p>
<table>
<thead>
<tr>
<th>Old Token</th>
<th>Old Value</th>
<th>Migration output</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>--dt-size-1115</code></td>
<td>1140px</td>
<td><code v-pre>71.25rem</code></td>
</tr>
<tr>
<td><code v-pre>--dt-size-1120</code></td>
<td>1268px</td>
<td><code v-pre>79.25rem</code></td>
</tr>
<tr>
<td><code v-pre>--dt-size-1125</code></td>
<td>1280px</td>
<td><code v-pre>80rem</code></td>
</tr>
<tr>
<td><code v-pre>--dt-size-1130</code></td>
<td>1340px</td>
<td><code v-pre>83.75rem</code></td>
</tr>
<tr>
<td><code v-pre>--dt-size-1150</code></td>
<td>1536px</td>
<td><code v-pre>96rem</code></td>
</tr>
<tr>
<td><code v-pre>--dt-size-1200</code></td>
<td>2048px</td>
<td><code v-pre>128rem</code></td>
</tr>
</tbody>
</table>
<p class="d-docsite--paragraph">If you need a token at one of these sizes, <a href="https://github.com/dialpad/dialtone/issues" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">open a request</a>.</p>
<h2 id="dt-spacing-tokens" tabindex="-1" class="d-docsite--header-2"><code v-pre>--dt-spacing-*</code> Tokens <a class="header-anchor d-link d-docsite--link d-link" href="#dt-spacing-tokens" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use spacing tokens for <code v-pre>padding</code>, <code v-pre>margin</code>, <code v-pre>gap</code>, <code v-pre>inset</code>, and similar whitespace properties.</p>
<p class="d-docsite--paragraph"><strong>Base unit:</strong> <code v-pre>--dt-spacing-base</code> = 8px. Scale stops multiply the base (e.g., <code v-pre>spacing-100</code> = 8px, <code v-pre>spacing-200</code> = 16px).</p>
<h3 id="mapping-from-dt-space" tabindex="-1" class="d-docsite--header-3">Mapping from <code v-pre>--dt-space-*</code> <a class="header-anchor d-link d-docsite--link d-link" href="#mapping-from-dt-space" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>Old Token</th>
<th>Old Value</th>
<th>New Token</th>
<th>New Value</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>--dt-space-0</code></td>
<td>0px</td>
<td><code v-pre>--dt-spacing-0</code></td>
<td>0px</td>
</tr>
<tr>
<td><code v-pre>--dt-space-100</code></td>
<td>1px</td>
<td><code v-pre>--dt-spacing-1</code></td>
<td>1px</td>
</tr>
<tr>
<td><code v-pre>--dt-space-200</code></td>
<td>2px</td>
<td><code v-pre>--dt-spacing-25</code></td>
<td>2px</td>
</tr>
<tr>
<td><code v-pre>--dt-space-300</code></td>
<td>4px</td>
<td><code v-pre>--dt-spacing-50</code></td>
<td>4px</td>
</tr>
<tr>
<td><code v-pre>--dt-space-350</code></td>
<td>6px</td>
<td><code v-pre>--dt-spacing-75</code></td>
<td>6px</td>
</tr>
<tr>
<td><code v-pre>--dt-space-400</code></td>
<td>8px</td>
<td><code v-pre>--dt-spacing-100</code></td>
<td>8px</td>
</tr>
<tr>
<td><code v-pre>--dt-space-450</code></td>
<td>12px</td>
<td><code v-pre>--dt-spacing-150</code></td>
<td>12px</td>
</tr>
<tr>
<td><code v-pre>--dt-space-500</code></td>
<td>16px</td>
<td><code v-pre>--dt-spacing-200</code></td>
<td>16px</td>
</tr>
<tr>
<td><code v-pre>--dt-space-525</code></td>
<td>20px</td>
<td><code v-pre>--dt-spacing-250</code></td>
<td>20px</td>
</tr>
<tr>
<td><code v-pre>--dt-space-550</code></td>
<td>24px</td>
<td><code v-pre>--dt-spacing-300</code></td>
<td>24px</td>
</tr>
<tr>
<td><code v-pre>--dt-space-600</code></td>
<td>32px</td>
<td><code v-pre>--dt-spacing-400</code></td>
<td>32px</td>
</tr>
<tr>
<td><code v-pre>--dt-space-625</code></td>
<td>42px</td>
<td><code v-pre>--dt-spacing-525</code></td>
<td>42px</td>
</tr>
<tr>
<td><code v-pre>--dt-space-650</code></td>
<td>48px</td>
<td><code v-pre>--dt-spacing-600</code></td>
<td>48px</td>
</tr>
<tr>
<td><code v-pre>--dt-space-700</code></td>
<td>64px</td>
<td><code v-pre>--dt-spacing-800</code></td>
<td>64px</td>
</tr>
</tbody>
</table>
<p class="d-docsite--paragraph">Tokens at <code v-pre>--dt-space-720</code> (72px), <code v-pre>--dt-space-730</code> (84px), and <code v-pre>--dt-space-750</code> and above (96px+) have no spacing equivalent — values at this scale belong in <code v-pre>--dt-layout-*</code>. The migration tool leaves these unchanged for manual review.</p>
<h2 id="migration-tool" tabindex="-1" class="d-docsite--header-2">Migration Tool <a class="header-anchor d-link d-docsite--link d-link" href="#migration-tool" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Run the migration helper from your project root:</p>
<div class="language-bash" data-highlighter="prismjs" data-ext="sh"><pre v-pre><code class="language-bash"><span class="line">npx dialtone-migration-helper <span class="token parameter variable">--cwd</span> ./src</span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">Two new configs are available:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong><code v-pre>space-to-spacing</code></strong> — migrates <code v-pre>--dt-space-*</code> → <code v-pre>--dt-spacing-*</code></li>
<li class="d-docsite--list-element"><strong><code v-pre>size-to-layout</code></strong> — migrates <code v-pre>--dt-size-*</code> → <code v-pre>--dt-layout-*</code> or <code v-pre>--dt-spacing-*</code> based on CSS property context (spacing properties route to <code v-pre>--dt-spacing-*</code>, layout properties route to <code v-pre>--dt-layout-*</code>)</li>
</ul>
<p class="d-docsite--paragraph">To apply changes without interactive confirmation:</p>
<div class="language-bash" data-highlighter="prismjs" data-ext="sh"><pre v-pre><code class="language-bash"><span class="line">npx dialtone-migration-helper <span class="token parameter variable">--cwd</span> ./src <span class="token parameter variable">--force</span></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph"><strong>Note:</strong> The previous <code v-pre>space-to-size</code> config has been removed. If you previously ran it and are now on <code v-pre>--dt-size-*</code>, run <code v-pre>size-to-layout</code> next. If you still have <code v-pre>--dt-space-*</code> tokens, run <code v-pre>space-to-spacing</code> directly — you can skip <code v-pre>space-to-size</code> entirely.</p>
<h2 id="lint-rule" tabindex="-1" class="d-docsite--header-2">Lint Rule <a class="header-anchor d-link d-docsite--link d-link" href="#lint-rule" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A new stylelint rule <code v-pre>@dialpad/stylelint-plugin-dialtone/no-deprecated-size-tokens</code> warns on any remaining <code v-pre>--dt-size-*</code> or <code v-pre>--dt-space-*</code> usage after migration. The rule message points to the appropriate migration helper config.</p>
<p class="d-docsite--paragraph">Thanks! — Dialtone Team 💜</p>
</BlogPost>
</div></template>


<script setup>
import BlogPost from '@baseComponents/BlogPost.vue';
import { parse } from 'date-fns';
</script>